import * as actions_invoices from 'actions/invoices-actions';
import * as toolbox from 'components/common/toolbox';
import * as actions_quickbooks from 'actions/quickbooks-actions';
import React, { useEffect, useState } from 'react';
import { APPS, SERVICE_REQUEST_TERMS } from 'components/constants';
import { ModalForm } from 'enspire-manager-framework';
import { Table } from 'em-table';
import { WORKORDERS } from 'components/constants';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

const moment = require('moment');
const _ = require('lodash');

const GenerateInvoiceForm = (props) => {
	
	/* Hooks ------------------------------------------------------------------------------------------------------------------------------------*/
	
	const params = useParams();
	const dispatch = useDispatch();
	const routeLocation = useLocation();
    const seg = 5;

	const customer = useSelector(store => store.profiles.profile);
	const employees = useSelector((store) => store.employees.employees);
	const features = useSelector((store) => store.settings?.settings?.features)?.[(params.appId == APPS.DISPATCH.id) ? 'dispatch' : 'assets'];
	const invoices = useSelector((store) => store.invoices);
	const quickbooksSettings = useSelector((store) => store.quickbooks.quickbooks);
	const serviceRequest = useSelector((store) => store.serviceRequests.service_request);;
	const settings = useSelector(store => store.settings.settings);
	const user = useSelector((store) => store.users.user);
	const workOrders = useSelector((store) => store.workOrders.work_orders);

	const [selectedWorkOrders, setSelectedWorkOrders] = useState([]);

    const depositInvoices = invoices.invoices?.filter((inv) => inv.isDeposit && !inv.isDepositApplied && inv.requestId == params.request_id) ?? [];
	const activeTaxRates = settings?.invoices?.tax_rates?.filter(rate => !rate.hidden) ?? [];
	const hasEmail = customer?.contacts?.some(contact => contact.email)?.length > 0;

	useEffect(() => {
		setSelectedWorkOrders(workOrders); 
	}, [workOrders]);
	
	/* Handlers ------------------------------------------------------------------------------------------------------------------------------------*/

	const handleSelect = (item) => {
		let newWorkOrders = (selectedWorkOrders) ? [...selectedWorkOrders] : [];
		let checked = !!selectedWorkOrders?.find((w) => w.id == item.id);

		if (!checked) newWorkOrders.push(item);
		else {
			let index = newWorkOrders.findIndex((wo) => wo.id == item.id);
			if (index > -1) { newWorkOrders.splice(index, 1); }
		}
		setSelectedWorkOrders(newWorkOrders);
	};
	const handleToggleAll = () => {
		let shouldCheck = (!selectedWorkOrders.length);
		let newWorkOrders = [];
		workOrders.forEach((wo) => {
			if (wo.statusId == WORKORDERS.COMPLETE.id && shouldCheck) {
				newWorkOrders.push(wo);
			}
		});
		setSelectedWorkOrders(newWorkOrders);
	}

	/* Actions ------------------------------------------------------------------------------------------------------------------------------------*/
	
	const submitForm = () => {
		if (selectedWorkOrders.length < 1) {
			return window.toastr.error('Please select at least one work order to include on the invoice.', "No Work Orders Selected");
		} else if (workOrderTotals - totalDepositAmount <= 0) {
			return window.toastr.error('The total of the selected work orders must be greater than the total deposit amount applied.', "Invalid Invoice Total");
		} else {
			dispatch(actions_invoices.createInvoice({
				activeTaxRates: activeTaxRates,
				appId: params.appId,
				depositInvoices: depositInvoices,
				handle: params.handle,
				request_id: params.request_id,
				serviceRequest: null,
				user: user,
				workOrders: selectedWorkOrders,
				callback: (invoiceId) => {
					dispatch(actions_quickbooks.updateQuickbooks({ handle: params.handle, type: 'invoice' }, quickbooksSettings));

					let location = (serviceRequest.serviceRequestTermsId == SERVICE_REQUEST_TERMS.SERVICE_CALL.id)
						?	toolbox.modifyPath(routeLocation.pathname, seg, `form/0/service_requests/${params.request_id}/work_orders/${params.work_order_id}/invoices/preview/${invoiceId}`)
						:	toolbox.modifyPath(routeLocation.pathname, seg, `form/0/service_requests/${params.request_id}/invoices/preview/${invoiceId}`)
					props.history.replace({ pathname: location });
				}
			}));
		}
	};
	
	/* Constants ------------------------------------------------------------------------------------------------------------------------------------*/

	const data = workOrders.map((wo) => {
		let technician = employees?.find((e) => e.id == wo.technicianId);
		return ({ ...wo,
			_stripe_color: Object.values(WORKORDERS).find((o) => o.id == wo.statusId)?.color,
			_no_checkbox: (wo.statusId != WORKORDERS.COMPLETE.id) ? ['checked'] : [],
			startDate: moment(wo.startDate.seconds, 'X').format('MM/DD/YYYY'),
			checked: !!selectedWorkOrders?.find((w) => w.id == wo.id),
			technician: (technician?.contact.firstName) ? technician?.contact.firstName + ' ' + technician?.contact.lastName : 'N/A',
			totalCost: wo?.totals?.grandTotal || 0,
		});
	});

	const workOrderTotals = selectedWorkOrders.reduce((sum, wo) => sum + (wo.totals?.grandTotal || 0), 0);
	const totalDepositAmount = depositInvoices.reduce((sum, inv) => sum + (inv.totals?.grandTotal || 0), 0);

	const columns = [
		{ name: 'Custom ID', field: 'customId', width: 20 },
		{ name: 'Technician', field: 'technician', width: 50 },
        { name: 'Total Cost', field: 'totalCost', type: 'number', format: 'usd', width: 20 },
        { name: '', field: 'checked', type: 'checkbox', callback: handleSelect, width: 10},
		{ name: 'Description', field: 'description' },
	];

	/* Render ------------------------------------------------------------------------------------------------------------------------------------*/

	return (

		<ModalForm {...props}
			history={props.history}
			modal_header={'Generate Invoice'}
			cancel_button_title="Cancel"
			save_button_title={'Generate Invoice'}
			submitFormHandler={submitForm}
			confirm_button_disabled={!selectedWorkOrders.length}
			show_spinner={invoices.invoice_save_pending}
		>
			<button className="btn btn-default btn-sm float-right mt-3" onClick={ handleToggleAll }>Toggle All</button>
			<h2>Select Work Order(s) to Include on the Invoice:</h2>

			{ features['quickbooks_integration'] && !hasEmail &&
				<div className="alert alert-danger mt-3 mb-0 d-flex align-items-center">
					<i className="fa-solid fa-exclamation-triangle fa-2x mr-3"></i>
					<span>A <strong>QuickBooks Payment Link</strong> cannot be generated for this Invoice. An email address could not be found in the the Customer's contact(s).</span>
				</div>
			}
			<div className="alert alert-info mt-3 mb-0 d-flex align-items-center">
				<i className="fa-solid fa-circle-info fa-2x mr-3"></i>
				<span>Only <strong>Completed</strong> Work Orders are able to be selected and included on the Invoice</span>
			</div>

			<Table
				data={data}
				show_limit={false}
				columns={columns}
				click_callback={handleSelect}
                order_by = {{ fields: ['id'], direction: ['desc'] }}
				second_line={'description'}
			/>

			<div className="col-12 d-flex justify-content-end">
				<table className="subtotal">
					<tbody>
						<tr>
							<td align="right">SUBTOTAL: </td>
							<td width="5"></td>
							<td align="right">{toolbox.formatNumber(workOrderTotals, 2, true)}</td>
							<td align="right" style={{width: '40px'}}></td>
						</tr>
						{ totalDepositAmount > 0 &&
							<tr>
								<td align="right">DEPOSIT APPLIED:</td>
								<td width="5"></td>
								<td align="right">{toolbox.formatNumber(totalDepositAmount, 2, true)}</td>
								<td align="right" style={{width: '40px'}}></td>
							</tr>
						}
						<tr><td colSpan={ 99 }><div className="mt-2"></div></td></tr>
						<tr>
							<td align="right" className="font-weight-bold">INVOICE TOTAL: </td>
							<td width="5"></td>
							<td align="right">{ toolbox.formatNumber(workOrderTotals - totalDepositAmount, 2, true) }</td>
							<td align="right" style={{width: '40px'}}></td>
						</tr>
					</tbody>
				</table>
			</div>
			
		</ModalForm>
	);
};


export default GenerateInvoiceForm;
